import { User, MapPin, Package, Heart, LogOut, ChevronRight, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { mockUser, mockOrders } from '@/data/mockData';
import { useWishlist } from '@/context/WishlistContext';
import { useAddress } from '@/context/AddressContext';
import { useAuth } from '@/context/AuthContext';
import { useEffect } from 'react';

const ProfilePage = () => {
  const { wishlistCount } = useWishlist();
  const { addresses, defaultAddress } = useAddress();
  const { user, setShowSignInModal } = useAuth();

  // Show sign-in modal if user is not authenticated
  useEffect(() => {
    if (!user.isAuthenticated) {
      setShowSignInModal(true);
    }
  }, [user.isAuthenticated, setShowSignInModal]);

  // If user is not authenticated, show sign-in prompt
  if (!user.isAuthenticated) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8"
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
                <UserPlus className="h-12 w-12 text-muted-foreground" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Sign In Required</h1>
              <p className="text-muted-foreground mb-6">
                Please sign in to access your profile, orders, and saved addresses.
              </p>
              <Button 
                onClick={() => setShowSignInModal(true)}
                size="lg"
                className="w-full"
              >
                Sign In to Continue
              </Button>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }

  const menuItems = [
    { icon: Package, label: 'My Orders', link: '/orders', count: mockOrders.length },
    { icon: Heart, label: 'Wishlist', link: '/wishlist', count: wishlistCount },
    { icon: MapPin, label: 'Saved Addresses', link: '/addresses', count: addresses.length },
  ];

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold mb-8">My Account</h1>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Profile Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:col-span-1"
          >
            <div className="bg-card rounded-xl shadow-card p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-rainbow flex items-center justify-center relative">
                <User className="h-12 w-12 text-primary-foreground" />
                {/* Green dot to indicate signed in */}
                <div className="absolute top-2 right-2 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <h2 className="text-xl font-semibold">
                {user.firstName && user.lastName 
                  ? `${user.firstName} ${user.lastName}` 
                  : user.firstName || user.email?.split('@')[0] || 'User'
                }
              </h2>
              <p className="text-muted-foreground">{user.email}</p>
              <p className="text-muted-foreground text-sm">{user.phoneNumber || 'Phone not added'}</p>
              
              <Button variant="outline" className="w-full mt-6">
                Edit Profile
              </Button>
            </div>
          </motion.div>

          {/* Menu & Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card rounded-xl shadow-card overflow-hidden"
            >
              {menuItems.map((item, index) => (
                <Link
                  key={item.label}
                  to={item.link}
                  className={`flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors ${
                    index !== menuItems.length - 1 ? 'border-b' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <span className="font-medium">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.count > 0 && (
                      <span className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full">
                        {item.count}
                      </span>
                    )}
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </motion.div>

            {/* Default Address */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card rounded-xl shadow-card p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Default Address</h3>
                <Link to="/addresses">
                  <Button variant="ghost" size="sm">Manage</Button>
                </Link>
              </div>
              
              {defaultAddress ? (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">{defaultAddress.fullName}</p>
                    <p className="text-muted-foreground text-sm">
                      {defaultAddress.addressLine1}
                      {defaultAddress.addressLine2 && `, ${defaultAddress.addressLine2}`}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {defaultAddress.city}, {defaultAddress.state} - {defaultAddress.postalCode}
                    </p>
                    <p className="text-muted-foreground text-sm mt-1">{defaultAddress.phoneNumber}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <MapPin className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground text-sm mb-3">No address saved yet</p>
                  <Link to="/address/add">
                    <Button variant="outline" size="sm">Add Address</Button>
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Logout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Button variant="outline" className="w-full text-destructive hover:bg-destructive/10">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
